import { clearingApi } from "../../config/apiConfig";

const BASE = "api/v1/thg/annual-summary";

/**
 * CLR-281 THG annual summary per station, for owner payouts.
 *
 * Every refusal the backend can give has its own remedy, so each is returned as a named `kind`
 * instead of collapsing into "something went wrong":
 * - 422: no THG rate configured for the year. Needs a config change, not a retry.
 * - 409: recompute of a year that is not final yet. The message carries the date it becomes final.
 * - 401/403: not an admin.
 * - 400: malformed or future year.
 * Neither function throws.
 */
const KIND_BY_STATUS = {
  400: "BAD_YEAR",
  401: "FORBIDDEN",
  403: "FORBIDDEN",
  409: "NOT_FINAL",
  422: "RATE_NOT_CONFIGURED",
};

/** @typedef {import("../../types/thgAnnualSummary").ThgResult} ThgResult */
/** @typedef {import("../../types/thgAnnualSummary").ThgFailure} ThgFailure */

/** @returns {ThgFailure} */
const toThgFailure = (error, context, fallback) => {
  console.error(`Failed to ${context}:`, error);
  const status = error?.response?.status ?? null;
  const body = error?.response?.data;
  return {
    success: false,
    kind: KIND_BY_STATUS[status] ?? "UNKNOWN",
    message: body?.error ?? fallback,
    status,
  };
};

/** @returns {Promise<ThgResult>} */
export const getThgAnnualSummary = async (year) => {
  try {
    const response = await clearingApi.get(`${BASE}/${year}`);
    return { success: true, data: response?.data };
  } catch (error) {
    return toThgFailure(
      error,
      `load the THG annual summary for ${year}`,
      `Could not load the THG payouts for ${year}.`,
    );
  }
};

/** @returns {Promise<ThgResult>} */
export const recomputeThgAnnualSummary = async (year) => {
  try {
    const response = await clearingApi.post(`${BASE}/${year}/recompute`);
    return { success: true, data: response?.data };
  } catch (error) {
    return toThgFailure(
      error,
      `recompute the THG annual summary for ${year}`,
      `Could not recompute the THG payouts for ${year}.`,
    );
  }
};
